import { useCallback, useEffect, useState } from 'react';

import { getPresetById, THEME_PRESETS, ThemePreset } from '../theme/custom-theme-presets';
import {
	deleteUserTheme,
	generateFullThemeFromColor,
	generateThemeFromColor,
	getUserThemes,
	isValidHexColor,
	saveUserTheme,
	UserGeneratedTheme,
} from '../theme/theme-generator';

const STORAGE_KEY = 'bonusly-custom-theme';

// Event types for theme changes
export interface ThemeChangeDetail {
	type: 'preset' | 'custom' | 'clear';
	presetId?: string;
	customTheme?: ThemePreset;
}

/**
 * Get the stored custom theme preference from localStorage
 */
const getStoredTheme = (): string | null => {
	if (typeof window === 'undefined') return null;
	return localStorage.getItem(STORAGE_KEY);
};

/**
 * Hook to manage custom theme preferences
 *
 * Provides access to theme presets and manages localStorage persistence.
 * Works alongside the CustomThemeInjector component which applies the actual CSS.
 * Now supports user-generated themes via chroma-js color generation.
 */
export const useCustomTheme = () => {
	const [activePresetId, setActivePresetId] = useState<string | null>(() => getStoredTheme());
	const [userThemes, setUserThemes] = useState<UserGeneratedTheme[]>(() => getUserThemes());
	const [activeCustomTheme, setActiveCustomTheme] = useState<ThemePreset | null>(null);

	// Sync state with localStorage on mount (for SSR safety)
	useEffect(() => {
		const stored = getStoredTheme();
		if (stored !== activePresetId) {
			setActivePresetId(stored);
		}

		// Check if the stored theme is a user-generated one
		if (stored?.startsWith('user-')) {
			const userTheme = getUserThemes().find((t) => t.id === stored);
			if (userTheme) {
				const generated = stored.startsWith('user-full-')
					? generateFullThemeFromColor(userTheme.baseColor, userTheme.name)
					: generateThemeFromColor(userTheme.baseColor, userTheme.name);
				setActiveCustomTheme(generated);
			}
		}
		// Only run on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Set the active theme preset (for built-in presets)
	 */
	const setTheme = useCallback((presetId: string) => {
		const preset = getPresetById(presetId);
		if (!preset) {
			console.warn(`[useCustomTheme] Preset "${presetId}" not found`);
			return;
		}

		localStorage.setItem(STORAGE_KEY, presetId);
		setActivePresetId(presetId);
		setActiveCustomTheme(null);

		// Dispatch a custom event so the injector can react immediately
		window.dispatchEvent(
			new CustomEvent<ThemeChangeDetail>('custom-theme-change', {
				detail: { type: 'preset', presetId },
			})
		);
	}, []);

	/**
	 * Generate and apply a custom theme from a hex color
	 */
	const generateAndApplyTheme = useCallback((baseColor: string, name: string = 'Custom', fullTheme: boolean = false) => {
		if (!isValidHexColor(baseColor)) {
			console.warn(`[useCustomTheme] Invalid color: "${baseColor}"`);
			return;
		}

		const generated = fullTheme ? generateFullThemeFromColor(baseColor, name) : generateThemeFromColor(baseColor, name);

		// Save the user theme
		const userTheme: UserGeneratedTheme = {
			id: generated.id,
			name,
			baseColor,
			createdAt: new Date().toISOString(),
		};
		saveUserTheme(userTheme);
		setUserThemes(getUserThemes());

		// Apply it
		localStorage.setItem(STORAGE_KEY, generated.id);
		setActivePresetId(generated.id);
		setActiveCustomTheme(generated);

		// Dispatch event with the full theme for the injector
		window.dispatchEvent(
			new CustomEvent<ThemeChangeDetail>('custom-theme-change', {
				detail: { type: 'custom', customTheme: generated },
			})
		);
	}, []);

	/**
	 * Delete a user-generated theme
	 */
	const removeUserTheme = useCallback(
		(themeId: string) => {
			deleteUserTheme(themeId);
			setUserThemes(getUserThemes());

			// If this was the active theme, clear it
			if (activePresetId === themeId) {
				localStorage.removeItem(STORAGE_KEY);
				setActivePresetId(null);
				setActiveCustomTheme(null);
				window.dispatchEvent(
					new CustomEvent<ThemeChangeDetail>('custom-theme-change', {
						detail: { type: 'clear' },
					})
				);
			}
		},
		[activePresetId]
	);

	/**
	 * Clear the custom theme and return to default
	 */
	const clearTheme = useCallback(() => {
		localStorage.removeItem(STORAGE_KEY);
		setActivePresetId(null);
		setActiveCustomTheme(null);

		// Dispatch event for injector
		window.dispatchEvent(
			new CustomEvent<ThemeChangeDetail>('custom-theme-change', {
				detail: { type: 'clear' },
			})
		);
	}, []);

	/**
	 * Get the active preset object (either built-in or user-generated)
	 */
	const activePreset: ThemePreset | null = activeCustomTheme ?? (activePresetId ? (getPresetById(activePresetId) ?? null) : null);

	return {
		/** Current active preset ID, or null if using default theme */
		activePresetId,
		/** Current active preset object, or null if using default theme */
		activePreset,
		/** All available built-in theme presets */
		presets: THEME_PRESETS,
		/** All user-generated themes */
		userThemes,
		/** Set a built-in theme by preset ID */
		setTheme,
		/** Generate and apply a custom theme from a hex color */
		generateAndApplyTheme,
		/** Delete a user-generated theme */
		removeUserTheme,
		/** Clear custom theme and return to default */
		clearTheme,
		/** Check if a preset is currently active */
		isActive: (presetId: string) => activePresetId === presetId,
		/** Check if user has any custom themes */
		hasUserThemes: userThemes.length > 0,
	};
};

export type UseCustomThemeReturn = ReturnType<typeof useCustomTheme>;
